import { useQueryClient } from "@tanstack/react-query";
import { Flex } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import { omit } from "lodash";
import { useState } from "react";
import FilterableTableLayout from "../../../../components/FilterableTableLayout/FilterableTableLayout";
import { PersistedFilterKeys } from "../../../../modules/filters/filters.types";
import {
  getTrackingListFilterItems,
  getTrackingTableColumns,
  TRACKING_LIST_INITIAL_FILTERS,
  TRACKING_LIST_INITIAL_PAGINATION,
} from "../../../../modules/trackAndTrace/trackAndTrace.constants";
import {
  useTrackingList,
  useTrackingListFiltersData,
} from "../../../../modules/trackAndTrace/trackAndTrace.queries";
import { trackAndTraceService } from "../../../../modules/trackAndTrace/trackAndTrace.service";
import {
  Tracking,
  TrackingListFilterPayload,
  TrackingListResponse,
} from "../../../../modules/trackAndTrace/trackAndTrace.types";
import { ApiResponseObject } from "../../../../types";
import style from "./TrackingPage.module.scss";
interface TrackingPageProps {
  isMfin?: boolean;
}

const TrackingPage = ({ isMfin }: TrackingPageProps) => {
  const [filters, setFilters] = useState<
    Partial<TrackingListFilterPayload> | undefined
  >(undefined);
  const { data: filtersData, isLoading: isLoadingFiltersData } =
    useTrackingListFiltersData();
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const renderCodeCell = (
    filters: Partial<TrackingListFilterPayload>,
    value: string | undefined
  ) => {
    return (
      <Flex
        onClick={(event) => {
          event.stopPropagation();
          setFilters(filters);
        }}
        className={style.codeCell}
      >
        {value}
      </Flex>
    );
  };

  const clearSastav = async (id: number) => {
    const result = await queryClient.getQueriesData({
      predicate(query) {
        return (
          query?.queryKey?.[1] === "trackingList" &&
          query?.queryKey?.[0] === "track_and_trace"
        );
      },
      stale: true,
    });

    const key = result?.[0]?.[0];

    setExpandedRowKeys((prevItems) => prevItems.filter((el) => el !== id));

    await queryClient.setQueryData(
      key,
      (oldData: ApiResponseObject<TrackingListResponse>) => {
        const currentList = oldData?.data?.list;

        const newArray = currentList?.map((element) => {
          if (element.id === id) {
            return omit(element, "children");
          }
          return element;
        });

        return { ...oldData, data: { ...oldData.data, list: newArray } };
      }
    );
  };

  const postTtIstorijaKodaSastav = async (
    id: number,
    tipPakovanjaId: number
  ) => {
    const [err, res] = await to(
      trackAndTraceService.postTtPracenjeKodaSastav({ id, tipPakovanjaId })
    );

    if (err || !res.success) return;

    const result = await queryClient.getQueriesData({
      predicate(query) {
        return (
          query?.queryKey?.[1] === "trackingList" &&
          query?.queryKey?.[0] === "track_and_trace"
        );
      },
      stale: true,
    });

    setExpandedRowKeys((prevItems) => [...prevItems, id]);

    const key = result?.[0]?.[0];

    await queryClient.setQueryData(
      key,
      (oldData: ApiResponseObject<TrackingListResponse>) => {
        const currentList = oldData?.data?.list;
        const newArray = currentList?.map((element) => {
          if (element.id === id) {
            const childrenArray = res?.data?.map((item) => ({
              ...item,
              nested: true,
              customId: (item.id ?? 0) + Math.random() * 31274123,
            }));

            return {
              ...element,
              ...(childrenArray?.length ? { children: childrenArray } : {}),
            };
          }
          return element;
        });

        return { ...oldData, data: { ...oldData.data, list: newArray } };
      }
    );
  };

  return (
    <FilterableTableLayout<
      Tracking,
      TrackingListResponse,
      TrackingListFilterPayload
    >
      tableName="tracking_table"
      toolbarId="tracking_list"
      dataListKey="list"
      className={style.trackingListTable}
      title={t("TRACK.PRACENJE_KODOVA")}
      getTableColumns={() =>
        getTrackingTableColumns({
          renderCodeCell,
          hiddenColumns: isMfin ? [] : ["pib", "nazivDrustva"],
          clearSastav,
          postTtIstorijaKodaSastav,
          className: style.trackingCell,
        })
      }
      scroll={{
        x: "fit-content",
      }}
      tableLayout="auto"
      queryHook={useTrackingList}
      tableRowKey="id"
      filterProps={{
        getFilterItems: () =>
          getTrackingListFilterItems({
            statuses: filtersData?.statuses ?? [],
            packageTypes: filtersData?.packageTypes ?? [],
            isLoading: isLoadingFiltersData,
            hiddenFilters: isMfin ? [] : ["pib"],
          }),
        initialFilters: TRACKING_LIST_INITIAL_FILTERS,
        persistedFilterKey: PersistedFilterKeys.TRACKING_LIST,
        outsideFilters: filters,
      }}
      paginationProps={{
        enableAPIPagination: true,
        initialPagination: TRACKING_LIST_INITIAL_PAGINATION,
        totalCountKey: "total",
      }}
      expandable={{
        expandRowByClick: true,
        showExpandColumn: false,
        rowExpandable: (row) => !!row.children?.length,
        expandedRowKeys: expandedRowKeys,
      }}
    />
  );
};

export default TrackingPage;
